import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import {
  Menu,
  X,
  ShoppingCart,
  User,
  Search,
  Heart,
  LogOut,
  UserCircle,
  Settings,
  Home,
  Mail,
  Bell,
  Sparkles,
  Store,
  ChevronDown,
  ChevronRight,
  Package,
  HelpCircle,
  Shield,
  Truck,
  ArrowRight,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../hooks/useCart";
import { useWishlist } from "../../hooks/useWishlist";
import { getPaymentResponse } from "../../utils/checkoutRecovery";
import MiniCart from "../common/MiniCart";
import SearchBar from "../shop/SearchBar";
import MobileSearchBar from "../shop/MobileSearchBar";
import {
  FaFacebook,
  FaInstagram,
  FaWhatsapp,
  FaMailBulk,
} from "react-icons/fa";

const PublicLayout = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [pendingPaymentBanner, setPendingPaymentBanner] = useState(null);

  // Safe hooks with error handling
  const authContext = useAuth();
  const cartContext = useCart();
  const wishlistContext = useWishlist();
  const navigate = useNavigate();
  const location = useLocation();

  // Show resume banner when payment confirmation may still be pending
  useEffect(() => {
    const payment = getPaymentResponse();
    if (!payment?.trackingToken) {
      setPendingPaymentBanner(null);
      return;
    }

    const dismissedKey = `checkout_pending_dismissed_${payment.trackingToken}`;
    if (sessionStorage.getItem(dismissedKey)) {
      setPendingPaymentBanner(null);
      return;
    }

    const path = location.pathname;
    const onCheckoutFlow =
      path.startsWith("/checkout/success") ||
      path.startsWith("/checkout/pending") ||
      path.startsWith("/checkout/failure");

    if (!onCheckoutFlow) {
      setPendingPaymentBanner(payment);
    } else {
      setPendingPaymentBanner(null);
    }
  }, [location.pathname]);

  // Extract values safely with fallbacks
  const { user, logout, isAuthenticated = false } = authContext || {};
  const { cart } = cartContext || {};
  const { wishlistCount } = wishlistContext || {};

  const cartLength = cart?.summary?.total_items ?? 0;

  // Safe user handling with default values
  const safeUser = user || {};
  const userFirstName = safeUser.first_name || "";
  const userLastName = safeUser.last_name || "";
  const userRole = safeUser.role || "";
  const userInitials =
    userFirstName?.[0] || userLastName?.[0]
      ? `${userFirstName?.[0] || ""}${userLastName?.[0] || ""}`
      : "U";

  // Scroll effect for navbar
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLogout = async () => {
    try {
      if (logout) {
        await logout();
      }
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
      // Still navigate to login even if logout fails
      navigate("/login");
    }
  };

  // Main navigation - Clear, specific wording following UX guidelines
  const navigation = [
    { name: "Home", href: "/", icon: Home, description: "Back to homepage" },
    {
      name: "Yarns",
      href: "/shop/yarns",
      icon: Store,
      description: "Browse yarns",
    },
    {
      name: "Accessories",
      href: "/shop/accessories",
      icon: Store,
      description: "Browse accessories",
    },
    {
      name: "About Us",
      href: "/about",
      icon: UserCircle,
      description: "Learn about our story",
    },
    {
      name: "Contact",
      href: "/contact",
      icon: Mail,
      description: "Get in touch with us",
    },
  ];

  // Utility navigation for secondary actions
  const utilityNavigation = [
    { name: "Help", href: "/help", icon: HelpCircle },
    { name: "Track Order", href: "/track", icon: Package },
    { name: "Support", href: "/support", icon: Shield },
  ];

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  // Popular searches data
  const popularSearches = [
    "Cotton Yarn",
    "Knitting Needles",
    "Crochet Hooks",
    "Baby Yarn",
    "Wool Yarn",
    "Acrylic Yarn",
  ];

  // Quick categories data
  const quickCategories = [
    { id: "yarn", name: "Yarn" },
    { id: "accessories", name: "Accessories" },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {pendingPaymentBanner && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-3">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm text-amber-900">
              Payment confirmation may still be processing. Resume to check your
              order status.
            </p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() =>
                  navigate(
                    `/checkout/pending?token=${pendingPaymentBanner.trackingToken}`,
                  )
                }
                className="text-sm font-medium text-amber-900 underline hover:no-underline"
              >
                Resume confirmation
              </button>
              <button
                type="button"
                onClick={() => {
                  sessionStorage.setItem(
                    `checkout_pending_dismissed_${pendingPaymentBanner.trackingToken}`,
                    "1",
                  );
                  setPendingPaymentBanner(null);
                }}
                className="text-sm text-amber-800 hover:text-amber-950"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Main Navigation Header */}
      <header
        className={`bg-white border-b border-gray-200 sticky top-0 z-50 transition-all duration-300`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Left Section - Logo at the edge */}
            <div className="flex items-center">
              <Link
                to="/"
                className="flex items-center group"
                aria-label="Knots & Krafts Homepage"
              >
                <img
                  src="/Knots&KraftsLogo.png"
                  alt="Knots & Krafts"
                  className="h-8 lg:h-10 w-auto transition-transform duration-300 group-hover:scale-105"
                />
                {/* <div className="ml-2 hidden md:block">
                  <h1 className="text-lg font-bold text-secondary-800 group-hover:text-primary-600 transition-colors">
                    Knots & Krafts
                  </h1>
                </div> */}
              </Link>
            </div>

            {/* Center Section - Navigation (Desktop only) */}
            <nav
              className="hidden lg:flex items-center justify-center flex-1 mx-6"
              role="navigation"
              aria-label="Main navigation"
            >
              <div className="flex items-center space-x-1">
                {navigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`relative group flex items-center px-4 py-2 text-base font-medium transition-all duration-300 ${
                        isActive
                          ? "text-primary-600"
                          : "text-secondary-700 hover:text-primary-600"
                      }`}
                      aria-current={isActive ? "page" : undefined}
                      title={item.description}
                    >
                      <item.icon className="w-4 h-4 mr-2 lg:hidden" />
                      <span>{item.name}</span>

                      {/* Active indicator */}
                      <span
                        className={`absolute bottom-0 left-1/2 h-0.5 bg-primary-600 transition-all duration-300 ${
                          isActive
                            ? "w-full -translate-x-1/2"
                            : "w-0 group-hover:w-full group-hover:-translate-x-1/2"
                        }`}
                      />
                    </Link>
                  );
                })}
              </div>
            </nav>

            {/* Right Section - Action Icons with better spacing */}
            <div className="flex items-center space-x-4">
              {/* Enhanced Search Button */}
              <button
                onClick={() => setSearchOpen(!searchOpen)}
                className={`relative p-3 rounded-xl transition-all duration-300 group ${
                  searchOpen
                    ? "bg-primary-100 text-primary-600 shadow-lg scale-105"
                    : "text-secondary-600 hover:text-primary-600 hover:bg-primary-50 hover:shadow-md"
                }`}
                aria-label="Search products"
                title="Search products"
              >
                <div className="relative">
                  <Search
                    className={`h-5 w-5 transition-all duration-300 ${
                      searchOpen ? "scale-110" : "group-hover:scale-110"
                    }`}
                  />

                  {/* Animated Search Pulse Effect */}
                  {searchOpen && (
                    <div className="absolute inset-0 rounded-full bg-primary-200 animate-ping opacity-75"></div>
                  )}

                  {/* Hover Glow Effect */}
                  <div className="absolute inset-0 rounded-full bg-primary-100 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>

                {/* Search Indicator */}
                {searchOpen && (
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-primary-500 rounded-full animate-pulse"></div>
                )}
              </button>

              {/* Wishlist - Hidden on mobile, better spacing */}
              <Link
                to="/wishlist"
                className="relative p-3 text-secondary-600 hover:text-primary-600 hover:bg-gray-100 transition-all duration-300 hidden sm:block"
                aria-label={`View wishlist (${wishlistCount} items)`}
                title="Wishlist"
              >
                <Heart className="h-5 w-5" />
                {wishlistCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold h-4 w-4 flex items-center justify-center text-[10px] rounded-full shadow-sm">
                    {wishlistCount > 99 ? "99+" : wishlistCount}
                  </span>
                )}
              </Link>

              {/* Cart Icon - Desktop Only */}
              <Link
                to="/cart"
                className="relative p-3 text-secondary-600 hover:text-primary-600 hover:bg-gray-100 transition-all duration-300 hidden lg:block"
                aria-label={`Shopping cart (${cartLength} items)`}
                title="Shopping cart"
              >
                <ShoppingCart className="h-5 w-5" />
                {cartLength > 0 && (
                  <span className="absolute -top-1 -right-1 bg-primary-600 text-white text-xs font-bold h-4 w-4 flex items-center justify-center text-[10px] rounded-full shadow-sm">
                    {cartLength > 99 ? "99+" : cartLength}
                  </span>
                )}
              </Link>

              {/* User Account with improved spacing - Hidden on mobile */}
              <div className="hidden lg:block">
                {isAuthenticated ? (
                  <div className="relative">
                    <button
                      onClick={() => setUserMenuOpen(!userMenuOpen)}
                      className="flex items-center p-3 text-secondary-600 hover:text-primary-600 hover:bg-gray-100 transition-all duration-300"
                      aria-label="User account menu"
                      aria-expanded={userMenuOpen}
                    >
                      <div className="h-6 w-6 bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white font-bold text-xs">
                        {userInitials}
                      </div>
                      <ChevronDown
                        className={`ml-2 h-3 w-3 transition-transform duration-300 hidden sm:block ${
                          userMenuOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {/* User Dropdown Menu */}
                    {userMenuOpen && (
                      <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 py-2 z-50">
                        {/* User Info Header */}
                        <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-primary-50 to-accent-50">
                          <div className="flex items-center space-x-3">
                            <div className="h-10 w-10 bg-gradient-to-r from-primary-600 to-accent-500 flex items-center justify-center text-white font-medium">
                              {userInitials}
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-secondary-900">
                                {userFirstName} {userLastName}
                              </p>
                              <p className="text-xs text-secondary-500 capitalize">
                                {userRole} Member
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Menu Items */}
                        <div className="py-2">
                          <Link
                            to="/profile"
                            className="flex items-center px-4 py-3 text-sm text-secondary-700 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                            onClick={() => setUserMenuOpen(false)}
                          >
                            <UserCircle className="mr-3 h-4 w-4" />
                            My Profile
                          </Link>

                          <Link
                            to="/orders"
                            className="flex items-center px-4 py-3 text-sm text-secondary-700 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                            onClick={() => setUserMenuOpen(false)}
                          >
                            <Package className="mr-3 h-4 w-4" />
                            Order History
                          </Link>
                          {userRole === "admin" && (
                            <Link
                              to="/admin"
                              className="flex items-center px-4 py-3 text-sm text-secondary-700 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                              onClick={() => setUserMenuOpen(false)}
                            >
                              <Settings className="mr-3 h-4 w-4" />
                              Admin Panel
                            </Link>
                          )}
                        </div>

                        {/* Logout Section */}
                        <div className="border-t border-gray-100 pt-2">
                          <button
                            onClick={handleLogout}
                            className="flex items-center w-full px-4 py-3 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 transition-colors"
                          >
                            <LogOut className="mr-3 h-4 w-4" />
                            Sign Out
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center space-x-3">
                    <Link
                      to="/login"
                      className="px-4 py-2 text-sm font-medium text-secondary-700 hover:text-primary-600 transition-colors"
                    >
                      Sign In
                    </Link>
                    <Link
                      to="/register"
                      className="px-4 py-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white text-sm font-medium hover:from-primary-700 hover:to-primary-800 transition-all duration-300"
                    >
                      Sign Up
                    </Link>
                  </div>
                )}
              </div>

              {/* Mobile menu button with better spacing */}
              <div className="lg:hidden ml-2">
                <button
                  type="button"
                  className="p-3 text-secondary-600 hover:text-primary-600 hover:bg-gray-100 transition-all duration-300"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  aria-label="Toggle main menu"
                  aria-expanded={mobileMenuOpen}
                >
                  {mobileMenuOpen ? (
                    <X className="h-5 w-5" />
                  ) : (
                    <Menu className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Search Modal */}
        {searchOpen && (
          <div className="fixed inset-0 z-50">
            <div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
              onClick={() => setSearchOpen(false)}
            />

            {/* Desktop Search Modal */}
            <div className="hidden lg:block fixed top-0 left-0 right-0 p-6 bg-white border-b border-gray-200 shadow-2xl">
              <div className="relative max-w-4xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 bg-gradient-to-r from-primary-100 to-accent-100 rounded-2xl">
                      <Search className="h-8 w-8 text-primary-600" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-secondary-900 font-jakarta">
                        Search Products
                      </h2>
                      <p className="text-gray-600 font-jakarta">
                        Find the perfect yarn, needles, and crafting supplies
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSearchOpen(false)}
                    className="p-3 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-all duration-300"
                    aria-label="Close search"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                {/* Enhanced Search Input with Auto-suggestions */}
                <SearchBar
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder="Search for yarn, needles, patterns..."
                  className="w-full"
                  showSuggestions={true}
                  onSuggestionSelect={(suggestion) => {
                    setSearchOpen(false);
                    setSearchQuery("");
                    // Navigate to product page
                    navigate(`/products/${suggestion.slug}`);
                  }}
                  onSearchSubmit={(query) => {
                    if (query.trim()) {
                      setSearchOpen(false);
                      setSearchQuery("");
                      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
                    }
                  }}
                />

                {/* Quick Search Suggestions */}
                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2 font-jakarta">
                      Popular Searches
                    </h3>
                    <div className="space-y-2">
                      {popularSearches.slice(0, 4).map((search) => (
                        <button
                          key={search}
                          onClick={() => {
                            setSearchQuery(search);
                            setSearchOpen(false);
                            navigate(`/search?q=${encodeURIComponent(search)}`);
                          }}
                          className="block w-full text-left px-3 py-2 text-sm text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-300 font-jakarta"
                        >
                          {search}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="text-center">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2 font-jakarta">
                      Quick Categories
                    </h3>
                    <div className="space-y-2">
                      {quickCategories.map((category) => (
                        <button
                          key={category.id}
                          onClick={() => {
                            setSearchOpen(false);
                            navigate(
                              category.id === "yarn"
                                ? "/shop/yarns"
                                : "/shop/accessories"
                            );
                          }}
                          className="block w-full text-left px-3 py-2 text-sm text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-300 font-jakarta"
                        >
                          {category.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Search Modal */}
            <div className="lg:hidden">
              <MobileSearchBar
                isOpen={searchOpen}
                onClose={() => setSearchOpen(false)}
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search for yarn, needles, patterns..."
                onSuggestionSelect={(suggestion) => {
                  setSearchOpen(false);
                  setSearchQuery("");
                  navigate(`/products/${suggestion.slug}`);
                }}
                onSearchSubmit={(query) => {
                  if (query.trim()) {
                    setSearchOpen(false);
                    setSearchQuery("");
                    navigate(`/search?q=${encodeURIComponent(query.trim())}`);
                  }
                }}
              />
            </div>
          </div>
        )}

        {/* Floating Mobile Search Button */}
        <div className="lg:hidden">
          <button
            onClick={() => setSearchOpen(true)}
            className={`fixed bottom-6 right-6 z-40 p-4 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-full shadow-2xl transition-all duration-500 transform hover:scale-110 hover:shadow-3xl ${
              scrolled
                ? "translate-y-0 opacity-100"
                : "translate-y-20 opacity-0"
            }`}
            aria-label="Quick search"
            title="Quick search"
          >
            <div className="relative">
              <Search className="h-6 w-6" />

              {/* Animated Ripple Effect */}
              <div className="absolute inset-0 rounded-full bg-white/20 animate-ping"></div>

              {/* Glow Effect */}
              <div className="absolute inset-0 rounded-full bg-primary-400 blur-xl opacity-50"></div>
            </div>
          </button>
        </div>

        {/* Modern Mobile Slide-out Menu */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
              onClick={() => setMobileMenuOpen(false)}
            />

            {/* Slide-out Menu - Sharp corners, no gradients */}
            <div className="fixed top-0 right-0 h-full w-80 max-w-sm bg-white transform transition-transform duration-300 ease-in-out animate-slide-in flex flex-col">
              {/* Sticky Menu Header */}
              <div className="sticky top-0 z-10 flex items-center justify-between p-4 border-b border-gray-200 bg-white">
                <div className="flex items-center space-x-3">
                  <img
                    src="/Knots&KraftsLogo.png"
                    alt="Knots & Krafts"
                    className="h-8 w-auto"
                  />
                  <h2 className="text-lg font-semibold text-secondary-900">
                    Menu
                  </h2>
                </div>
                <div className="flex items-center space-x-2">
                  {/* Mobile Search Button */}
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setSearchOpen(true);
                    }}
                    className="p-2 text-secondary-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    aria-label="Search products"
                    title="Search products"
                  >
                    <Search className="h-5 w-5" />
                  </button>

                  {/* Close Menu Button */}
                  <button
                    onClick={() => setMobileMenuOpen(false)}
                    className="p-2 text-secondary-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    aria-label="Close menu"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Menu Content */}
              <div className="flex-1 px-4 py-6 space-y-8 overflow-y-auto pb-24">
                {/* User Info */}
                {isAuthenticated && (
                  <div className="flex items-center space-x-4 p-4 bg-gray-50 border border-gray-200 mb-2">
                    <div className="h-12 w-12 bg-primary-600 flex items-center justify-center">
                      <span className="text-lg font-medium text-white">
                        {userInitials}
                      </span>
                    </div>
                    <div>
                      <p className="text-base font-semibold text-secondary-900">
                        {userFirstName} {userLastName}
                      </p>
                      <p className="text-sm text-secondary-500 capitalize">
                        {userRole.charAt(0).toUpperCase() + userRole.slice(1)}
                      </p>
                    </div>
                  </div>
                )}

                {/* Primary Navigation - Clear hierarchy with sections */}
                <div className="space-y-1">
                  <h3 className="text-xs font-semibold text-secondary-500 uppercase tracking-wider px-4 py-2">
                    Main Navigation
                  </h3>
                  {navigation.map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`flex items-center px-4 py-4 text-lg font-medium transition-all duration-300 ${
                          isActive
                            ? "bg-primary-100 text-primary-600 border-r-4 border-primary-600"
                            : "text-secondary-700 hover:text-primary-600 hover:bg-gray-50"
                        }`}
                        onClick={() => setMobileMenuOpen(false)}
                        aria-current={isActive ? "page" : undefined}
                      >
                        <item.icon className="mr-3 h-6 w-6" />
                        <span className="flex-1">{item.name}</span>
                        {isActive && (
                          <ChevronRight className="h-5 w-5 text-primary-600" />
                        )}
                      </Link>
                    );
                  })}
                </div>

                {/* Divider */}
                <div className="border-t border-gray-200 my-2" />

                {/* Account Actions - Clear grouping */}
                {isAuthenticated && (
                  <div className="pt-2 space-y-1">
                    <h3 className="text-xs font-semibold text-secondary-500 uppercase tracking-wider px-4 py-2">
                      Account
                    </h3>
                    <Link
                      to="/profile"
                      className="flex items-center px-4 py-4 text-lg font-medium text-secondary-700 hover:text-primary-600 hover:bg-gray-50 transition-all duration-300"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <UserCircle className="mr-3 h-6 w-6" />
                      <span className="flex-1">My Profile</span>
                    </Link>

                    <Link
                      to="/orders"
                      className="flex items-center px-4 py-4 text-lg font-medium text-secondary-700 hover:text-primary-600 hover:bg-gray-50 transition-all duration-300"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Package className="mr-3 h-6 w-6" />
                      <span className="flex-1">Order History</span>
                    </Link>

                    {userRole === "admin" && (
                      <Link
                        to="/admin"
                        className="flex items-center px-4 py-4 text-lg font-medium text-secondary-700 hover:text-primary-600 hover:bg-gray-50 transition-all duration-300"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Settings className="mr-3 h-6 w-6" />
                        <span className="flex-1">Admin Panel</span>
                      </Link>
                    )}

                    <div className="border-t border-gray-100 mt-4 pt-4">
                      <button
                        onClick={() => {
                          handleLogout();
                          setMobileMenuOpen(false);
                        }}
                        className="flex items-center w-full px-4 py-4 text-lg font-medium text-red-600 hover:text-red-700 hover:bg-red-50 transition-all duration-300"
                      >
                        <LogOut className="mr-3 h-6 w-6" />
                        <span className="flex-1">Sign Out</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Authentication - Clear call-to-action */}
                {!isAuthenticated && (
                  <div className="pt-2 space-y-1">
                    <h3 className="text-xs font-semibold text-secondary-500 uppercase tracking-wider px-4 py-2">
                      Get Started
                    </h3>
                    <Link
                      to="/login"
                      className="flex items-center px-4 py-4 text-lg font-medium text-secondary-700 hover:text-primary-600 hover:bg-gray-50 transition-all duration-300"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <User className="mr-3 h-6 w-6" />
                      <span className="flex-1">Sign In</span>
                      <ChevronRight className="h-5 w-5" />
                    </Link>
                    <Link
                      to="/register"
                      className="flex items-center px-4 py-4 text-lg font-medium bg-primary-600 text-white hover:bg-primary-700 transition-all duration-300 mx-4 mt-2"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Sparkles className="mr-3 h-6 w-6" />
                      <span className="flex-1">Create Account</span>
                      <ChevronRight className="h-5 w-5" />
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1 pb-20 md:pb-0 safe-area-pb">
        <Outlet />
      </main>

      {/* Enhanced Mobile Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-lg border-t border-gray-200/80 lg:hidden z-50 safe-area-pb shadow-lg">
        <div className="flex justify-around items-center h-18 px-1 sm:px-2">
          {/* Home */}
          <Link
            to="/"
            className={`flex flex-col items-center justify-center space-y-1 px-1 sm:px-2 py-2 rounded-lg transition-all duration-300 min-w-0 flex-1 ${
              location.pathname === "/"
                ? "text-primary-600 bg-primary-50/50"
                : "text-gray-600 hover:text-primary-600 hover:bg-gray-50/50"
            }`}
          >
            <Home className="h-5 w-5 sm:h-6 sm:w-6" />
            <span className="text-xs sm:text-sm font-medium truncate">
              Home
            </span>
          </Link>

          {/* Search */}
          <button
            onClick={() => setSearchOpen(!searchOpen)}
            className="flex flex-col items-center justify-center space-y-1 px-1 sm:px-2 py-2 rounded-lg text-gray-600 hover:text-primary-600 hover:bg-gray-50/50 transition-all duration-300 min-w-0 flex-1"
          >
            <Search className="h-5 w-5 sm:h-6 sm:w-6" />
            <span className="text-xs sm:text-sm font-medium truncate">
              Search
            </span>
          </button>

          {/* Cart */}
          <div className="relative flex-1 min-w-0">
            <Link
              to="/cart"
              className="flex flex-col items-center justify-center space-y-1 px-1 sm:px-2 py-2 rounded-lg text-gray-600 hover:text-primary-600 hover:bg-gray-50/50 transition-all duration-300"
            >
              <div className="relative">
                <ShoppingCart className="h-5 w-5 sm:h-6 sm:w-6" />
                {cartLength > 0 && (
                  <span className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 bg-primary-600 text-white text-xs font-bold h-4 w-4 sm:h-5 sm:w-5 flex items-center justify-center rounded-full shadow-sm">
                    {cartLength > 99 ? "99+" : cartLength}
                  </span>
                )}
              </div>
              <span className="text-xs sm:text-sm font-medium truncate">
                Cart
              </span>
            </Link>
          </div>

          {/* Profile/Login */}
          {isAuthenticated ? (
            <Link
              to="/profile"
              className={`flex flex-col items-center justify-center space-y-1 px-1 sm:px-2 py-2 rounded-lg transition-all duration-300 min-w-0 flex-1 ${
                location.pathname === "/profile"
                  ? "text-primary-600 bg-primary-50/50"
                  : "text-gray-600 hover:text-primary-600 hover:bg-gray-50/50"
              }`}
            >
              <div className="h-5 w-5 sm:h-6 sm:w-6 bg-gradient-to-r from-primary-500 to-primary-600 flex items-center justify-center text-white font-bold text-xs sm:text-sm rounded-full shadow-sm">
                {userInitials}
              </div>
              <span className="text-xs sm:text-sm font-medium truncate">
                Profile
              </span>
            </Link>
          ) : (
            <Link
              to="/login"
              className="flex flex-col items-center justify-center space-y-1 px-1 sm:px-2 py-2 rounded-lg text-gray-600 hover:text-primary-600 hover:bg-gray-50/50 transition-all duration-300 min-w-0 flex-1"
            >
              <User className="h-5 w-5 sm:h-6 sm:w-6" />
              <span className="text-xs sm:text-sm font-medium truncate">
                Login
              </span>
            </Link>
          )}
        </div>

        {/* Active indicator - Home / Search / Cart / Profile (hidden when no tab applies) */}
        <div
          className="absolute bottom-0 left-0 h-1 bg-primary-600 transition-all duration-300 pointer-events-none"
          style={{
            width: "25%",
            opacity: (() => {
              if (searchOpen) return 1;
              if (location.pathname === "/") return 1;
              if (location.pathname.startsWith("/cart")) return 1;
              if (
                location.pathname.startsWith("/profile") ||
                location.pathname.startsWith("/login") ||
                location.pathname.startsWith("/register")
              )
                return 1;
              return 0;
            })(),
            transform: `translateX(${
              searchOpen
                ? "25%"
                : location.pathname === "/"
                ? "0%"
                : location.pathname.startsWith("/cart")
                ? "50%"
                : location.pathname.startsWith("/profile") ||
                  location.pathname.startsWith("/login") ||
                  location.pathname.startsWith("/register")
                ? "75%"
                : "0%"
            })`,
          }}
        ></div>
      </nav>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            {/* Company Info */}
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center mb-6">
                <img
                  src="/Knots&KraftsLogo.png"
                  alt="Logo"
                  className="h-10 w-auto mr-4"
                />
              </div>
              <p className="text-gray-300 mb-6 max-w-md leading-relaxed">
                Your one-stop destination for premium crafting supplies,
                creative materials, and artistic inspiration. Bringing your
                creative visions to life with quality materials and expert
                guidance.
              </p>
              <div className="flex space-x-4">
                <a
                  href="https://www.instagram.com/knotsandkrafts/?hl=en"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-violet-500 flex items-center justify-center rounded-full transition transform hover:-translate-y-1 hover:shadow-lg"
                  aria-label="Instagram"
                >
                  <FaInstagram size={20} className="text-white" />
                </a>
                <a
                  href="https://wa.me/9480424751"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-green-500 flex items-center justify-center rounded-full transition transform hover:-translate-y-1 hover:shadow-lg"
                  aria-label="WhatsApp"
                >
                  <FaWhatsapp size={20} className="text-white" />
                </a>
                <a
                  href="https://m.facebook.com/knots.krafts/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-blue-600 flex items-center justify-center rounded-full transition transform hover:-translate-y-1 hover:shadow-lg"
                  aria-label="Facebook"
                >
                  <FaFacebook size={20} className="text-white" />
                </a>
                <a
                  href="mailto:knots.krafts@gmail.com"
                  className="w-12 h-12 bg-red-500 flex items-center justify-center rounded-full transition transform hover:-translate-y-1 hover:shadow-lg"
                  aria-label="Email"
                >
                  <FaMailBulk size={20} className="text-white" />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-lg font-semibold mb-4 text-white">
                Quick Links
              </h3>
              <ul className="space-y-3">
                <li>
                  <Link
                    to="/"
                    className="text-gray-300 hover:text-white transition-colors duration-200 font-medium hover:translate-x-1 transform inline-block"
                  >
                    HOME
                  </Link>
                </li>
                <li>
                  <Link
                    to="/about"
                    className="text-gray-300 hover:text-white transition-colors duration-200 font-medium hover:translate-x-1 transform inline-block"
                  >
                    ABOUT
                  </Link>
                </li>
                <li>
                  <Link
                    to="/contact"
                    className="text-gray-300 hover:text-white transition-colors duration-200 font-medium hover:translate-x-1 transform inline-block"
                  >
                    CONTACT
                  </Link>
                </li>
                <li>
                  <Link
                    to="/faq"
                    className="text-gray-300 hover:text-white transition-colors duration-200 font-medium hover:translate-x-1 transform inline-block"
                  >
                    FAQ
                  </Link>
                </li>
                <li>
                  <Link
                    to="/refund-policy"
                    className="text-gray-300 hover:text-white transition-colors duration-200 font-medium hover:translate-x-1 transform inline-block"
                  >
                    REFUND POLICY
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-700 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © {new Date().getFullYear()} Knots & Krafts. All rights reserved.
            </p>
            <div className="flex space-x-8 mt-4 md:mt-0">
              <Link
                to="/privacy-policy"
                className="text-gray-400 hover:text-white text-sm transition-colors duration-200 hover:underline"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms-of-service"
                className="text-gray-400 hover:text-white text-sm transition-colors duration-200 hover:underline"
              >
                Terms of Service
              </Link>
              <Link
                to="/cookie-policy"
                className="text-gray-400 hover:text-white text-sm transition-colors duration-200 hover:underline"
              >
                Cookie Policy
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;
